import { useState } from "react";
import { useSelector } from "react-redux";
import { AxiosResponse } from "axios";
import { RootState } from "../redux/reducers/index";
import { DataService } from "../utils/dataService";
import { viewTotalDiscountAmount } from "../utils/formatData";
import { path } from "../config/enviroment";
import { uniq } from "lodash";
import { language } from "../utils/language";
import { matchesTemplate } from "../helpers/helper";
import { error } from "../notifications/displayMessage";
import { isFreeShippingCoupon } from "../utils/discountTypes";

type UseDiscountCouponsParams = {
  cartId: string;
  catalogueId: string;
  items: any[];
  appliedCoupons: Coupon[];
  setAppliedCoupons: any;
  subTotalAmount: number;
  taxAmountTotal: number;
  consumptionTaxAmountTotal: number;
};

export function useDiscountCoupons({
  cartId,
  catalogueId,
  items,
  appliedCoupons,
  setAppliedCoupons,
  subTotalAmount,
  taxAmountTotal,
  consumptionTaxAmountTotal,
}: UseDiscountCouponsParams) {
  const homeState: HomeStateType = useSelector(
    (state: RootState) => state.home
  );

  const [couponCode, setCouponCode] = useState("");
  const [showCouponError, setShowCouponError] = useState(false);
  const [couponErrorMessages, setCouponErrorMessages] = useState<string[]>([]);
  const [loadCode, setLoadCode] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCouponCode(event.target.value);
  };

  const clearCouponCode = () => {
    setCouponCode("");
  };

  const couponValidator = (coupons: Coupon[]): string[] => {
    let errors: string[] = [];
    for (const coupon of coupons) {
      let errorsItem = viewTotalDiscountAmount(
        items,
        [coupon],
        subTotalAmount + taxAmountTotal + consumptionTaxAmountTotal,
        "errors",
        homeState.currency,
        homeState.default_language
      );
      if (errorsItem.length > 0) {
        errors = [...errors, ...errorsItem];
      }
    }

    return uniq(errors);
  };

  const validateCouponCode = (code: string): Promise<Coupon | any> => {
    setCouponErrorMessages([]);
    return new Promise((resolve, reject) => {
      setLoadCode(true);
      const _dataService = new DataService(homeState.default_language);
      _dataService
        .post("/catalogue/apply-discount-codes", {
          url: path,
          shoppingCartId: cartId,
          discountCode: code,
          catalogueId: catalogueId,
          filter: {
            origin: "epayco",
          },
        })
        .then((res: AxiosResponse) => {
          let data;
          let hasError = false;
          if (res.data.success) {
            data = res.data.data;
          } else if (res.data.data.errors.length > 0) {
            let errors: string[] = [];

            for (const error of res.data.data.errors) {
              const matched = Object.values(
                language[homeState.default_language]
              ).some((template) =>
                matchesTemplate(error.errorMessage, template as string)
              );

              const codeError5 = matchesTemplate(
                error.errorMessage,
                language[homeState.default_language]["errorCode5"]
              );

              if (matched && !codeError5) {
                errors.push(error.errorMessage);
              } else if (matched && codeError5) {
                errors.push(error.errorMessage + " " + homeState.currency);
              }
            }

            let response = {
              data: errors,
              errors: true,
            };

            hasError = true;

            resolve(response);
          }

          if (data !== null && !hasError) {
            const mockCoupon: any = {
              data: {
                id: data.id,
                code: data.name,
                type: data.discountType,
                value: data.discountAmount,
                minCartAmount: data.shoppingCarAmount,
                quantity: data.remainingQuantity,
                validUntil: data.endDate,
                validInitDate: data.startDate,
                allowedCategories: JSON.parse(data.categories) ?? [],
                allowCombination: data.combineCode,
                filterAmount: data.filterAmount,
                filterShopping: data.filterShopping,
                filterCategory: data.filterCategory,
                filterTimeframe: data.filterTimeframe,
              },
              errors: false,
            };
            resolve(mockCoupon);
          }
          resolve(null);
          setLoadCode(false);
        })
        .catch((err) => {
          setLoadCode(false);
          error(`${language[homeState.default_language]["errorCode4"]}`);
        });
    });
  };

  const handleApplyCoupon = () => {
    let item = appliedCoupons.find(
      (itemCoupon) =>
        itemCoupon.code === couponCode || !itemCoupon.allowCombination
    );

    if (!item) {
      validateCouponCode(couponCode).then((coupon) => {
        if (!coupon.errors) {
          let validationErrors = couponValidator([
            ...appliedCoupons,
            coupon.data,
          ]);

          let result: any = viewTotalDiscountAmount(
            items,
            [...appliedCoupons, coupon.data],
            subTotalAmount + taxAmountTotal + consumptionTaxAmountTotal,
            "",
            "COP",
            homeState.default_language
          );
          if (result.errors.length === 0) {
            let total =
              subTotalAmount +
              taxAmountTotal +
              consumptionTaxAmountTotal -
              result.amount;
            if (!isFreeShippingCoupon(coupon.data) && (total <= 0 || result.amount == 0)) {
              setCouponErrorMessages((prevData: any) => [
                ...prevData,
                `*${language[homeState.default_language]["errorCode7"]}.`,
              ]);
              return;
            }

            if (
              appliedCoupons.length > 1 &&
              coupon.data.allowCombination === 0
            ) {
              validationErrors.push(
                `*${language[homeState.default_language]["errorCode"]}.`
              );

              coupon.data.error = validationErrors.length > 0;
              setCouponErrorMessages(validationErrors);
              setShowCouponError(validationErrors.length > 0);
            } else {
              setAppliedCoupons((prevCoupons: any) => [
                ...prevCoupons,
                coupon.data,
              ]);
              clearCouponCode();
            }
          } else {
            setCouponErrorMessages(uniq(result.errors));
            return;
          }
        } else {
          for (const el of coupon.data) {
            setCouponErrorMessages([`*${el}.`]);
          }

          setShowCouponError(true);
        }
      });
    } else {
      setCouponErrorMessages(
        uniq([
          ...couponErrorMessages,
          item.allowCombination
            ? `*${language[homeState.default_language]["errorCode3"]}`
            : `*${language[homeState.default_language]["errorCode"]}`,
        ])
      );
      setShowCouponError(true);
    }
  };

  const handleCloseCoupon = (index: number) => {
    setShowCouponError(false);
    setCouponErrorMessages([]);
    clearCouponCode();
    setAppliedCoupons((prevCoupons: any) =>
      prevCoupons.filter((_: any, i: any) => i !== index)
    );
  };

  return {
    couponCode,
    showCouponError,
    couponErrorMessages,
    loadCode,
    handleInputChange,
    handleApplyCoupon,
    handleCloseCoupon,
  };
}
